// src/component/AdminDayBoundary.js
// 日付切り替え時刻（JST）の管理画面
// Firestore: appConfig/dayBoundary { hour: 3, minute: 0 }

import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const DOC_PATH = "appConfig/dayBoundary";

export default function AdminDayBoundary() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";

  const [loading, setLoading] = useState(true);
  const [remote, setRemote] = useState({ hour: 3, minute: 0 });
  const [hour, setHour] = useState("3");
  const [minute, setMinute] = useState("0");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // Firestoreからリアルタイム取得
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "appConfig", "dayBoundary"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const h = typeof data.hour === "number" ? data.hour : 3;
        const m = typeof data.minute === "number" ? data.minute : 0;
        setRemote({ hour: h, minute: m });
        setHour(String(h));
        setMinute(String(m));
        setDirty(false);
        setLoading(false);
      },
      (err) => {
        console.error("[AdminDayBoundary] onSnapshot error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleHourChange = (e) => {
    setHour(e.target.value);
    setDirty(true);
    setSaveMsg("");
  };

  const handleMinuteChange = (e) => {
    setMinute(e.target.value);
    setDirty(true);
    setSaveMsg("");
  };

  const parsedHour = Math.min(23, Math.max(0, Math.floor(Number(hour) || 0)));
  const parsedMinute = Math.min(59, Math.max(0, Math.floor(Number(minute) || 0)));
  const previewTime = `${String(parsedHour).padStart(2, "0")}:${String(parsedMinute).padStart(2, "0")}`;

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      await setDoc(doc(db, "appConfig", "dayBoundary"), {
        hour: parsedHour,
        minute: parsedMinute,
        updatedAt: serverTimestamp(),
      });
      setSaveMsg("保存しました");
      setDirty(false);
    } catch (err) {
      console.error("[AdminDayBoundary] save error:", err);
      setSaveMsg("保存に失敗しました: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 520 }}>
      <Typography
        variant="overline"
        sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
      >
        ADMIN
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase, mb: 0.5 }}
      >
        日付切り替え時刻
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 3 }}>
        音楽再生カウント（TODAY等）の日付が切り替わるJST時刻を設定します。
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2, color: textBase, fontWeight: 700 }}>
          切り替え時刻（JST）
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="時"
            type="number"
            size="small"
            value={hour}
            onChange={handleHourChange}
            inputProps={{ min: 0, max: 23 }}
            sx={{ width: 100 }}
          />
          <Typography variant="h6" sx={{ color: textBase }}>:</Typography>
          <TextField
            label="分"
            type="number"
            size="small"
            value={minute}
            onChange={handleMinuteChange}
            inputProps={{ min: 0, max: 59 }}
            sx={{ width: 100 }}
          />
        </Stack>

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: `${accent}08`,
            border: `1px solid ${accent}22`,
          }}
        >
          <Typography variant="body2" sx={{ color: textBase }}>
            現在の設定: <strong>JST {String(remote.hour).padStart(2, "0")}:{String(remote.minute).padStart(2, "0")}</strong>
          </Typography>
          {dirty && (
            <Typography variant="body2" sx={{ color: accent, mt: 0.5 }}>
              変更後: <strong>JST {previewTime}</strong>
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "rgba(0,0,0,0.45)" }}>
          例: 3:00 に設定すると、午前3時を境に「今日」が切り替わります。
          深夜0:00〜2:59の再生は前日としてカウントされます。
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
            disabled={!dirty || saving}
            onClick={handleSave}
            sx={{
              textTransform: "none",
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            }}
          >
            保存
          </Button>
        </Stack>

        {saveMsg && (
          <Alert
            severity={saveMsg.startsWith("保存しました") ? "success" : "error"}
            sx={{ mt: 2 }}
          >
            {saveMsg}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
